import {
  FreightLocationType,
  FreightLocationTypeLabels,
} from "@redotech/redo-fulfillment-carrier-common/freight";
import { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import * as styles from "./address-card.module.css";

const locationTypeOptions: RedoListItem<FreightLocationType>[] = Object.values(
  FreightLocationType,
).map((type) => ({
  type: "text" as const,
  id: type,
  value: type,
  text: FreightLocationTypeLabels[type],
}));

interface AddressCardProps {
  title: string;
  address: FulfillmentOrderAddress;
  onEdit?(): void;
  warning?: string;
  locationType?: FreightLocationType;
  onLocationTypeChange?(locationType: FreightLocationType): void;
}

export const AddressCard = memo(function AddressCard({
  title,
  address,
  onEdit,
  warning,
  locationType,
  onLocationTypeChange,
}: AddressCardProps) {
  const addressLine = [address.address1, address.address2]
    .filter(Boolean)
    .join(", ");
  const cityStateZip = [
    address.city,
    [address.province, address.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Flex dir="column" gap="xl">
      <Flex align="center" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {title}
        </Text>
        {onEdit && (
          <RedoButton
            hierarchy="link_underline"
            onClick={onEdit}
            size="xs"
            text="Edit"
          />
        )}
      </Flex>
      {warning && (
        <Text fontSize="xs" textColor="warning">
          {warning}
        </Text>
      )}
      <Flex dir="column" gap="none">
        {addressLine && (
          <Text fontSize="xs" textColor="primary">
            {addressLine}
          </Text>
        )}
        {cityStateZip && (
          <Text fontSize="xs" textColor="primary">
            {cityStateZip}
          </Text>
        )}
      </Flex>

      {(address.name || address.phone) && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Contact
          </Text>
          {address.name && (
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              {address.name}
            </Text>
          )}
          {address.phone && (
            <Text fontSize="xxs" textColor="secondary">
              {address.phone}
            </Text>
          )}
        </Flex>
      )}
      {onLocationTypeChange && (
        <LocationTypeDropdown
          locationType={locationType}
          onLocationTypeChange={onLocationTypeChange}
        />
      )}
      <Flex className={styles.divider} />
    </Flex>
  );
});

const LocationTypeDropdown = memo(function LocationTypeDropdown({
  locationType,
  onLocationTypeChange,
}: {
  locationType?: FreightLocationType;
  onLocationTypeChange(locationType: FreightLocationType): void;
}) {
  const selectedOption = useMemo(
    () => locationTypeOptions.find((o) => o.value === locationType),
    [locationType],
  );

  return (
    <RedoSingleSelectDropdownInput
      label="Location type"
      options={locationTypeOptions}
      optionSelected={({ item }: { item: RedoListItem<FreightLocationType> }) =>
        onLocationTypeChange(item.value)
      }
      placeholder="Select location type"
      selectedItem={selectedOption}
      size="xs"
    />
  );
});
